import React, { useState } from 'react';
import '../App.css';
import { auth } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

/* ─── SVG Pokéball (reused from Rules) ──────────────────────────────────────── */
function HeroGraphic({ size = 140 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 220 220" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="110" cy="110" r="100" stroke="url(#ringGradL)" strokeWidth="1.5" strokeDasharray="6 4" opacity="0.5"/>
      <circle cx="110" cy="110" r="84" stroke="rgba(200,168,75,0.15)" strokeWidth="1"/>
      <circle cx="110" cy="110" r="62" fill="rgba(20,26,40,0.9)" stroke="url(#ballGradL)" strokeWidth="2.5"/>
      <path d="M48 110 A62 62 0 0 1 172 110 Z" fill="url(#topGradL)" opacity="0.85"/>
      <rect x="48" y="106" width="124" height="8" fill="#0e1117"/>
      <rect x="48" y="107.5" width="124" height="1" fill="rgba(200,168,75,0.5)"/>
      <rect x="48" y="111.5" width="124" height="1" fill="rgba(200,168,75,0.5)"/>
      <circle cx="110" cy="110" r="14" fill="#0e1117" stroke="url(#btnGradL)" strokeWidth="2.5"/>
      <circle cx="110" cy="110" r="8" fill="url(#innerBtnL)"/>
      <circle cx="106" cy="106" r="2" fill="rgba(255,255,255,0.3)"/>
      {[0,60,120,180,240,300].map((deg, i) => {
        const r = 96;
        const x = 110 + r * Math.cos((deg * Math.PI) / 180);
        const y = 110 + r * Math.sin((deg * Math.PI) / 180);
        return <circle key={i} cx={x} cy={y} r="2" fill="#c8a84b" opacity={0.4 + (i % 3) * 0.2}/>;
      })}
      <path d="M140 58 L133 74 L139 74 L130 92 L145 72 L138 72 Z" fill="#f0d060" opacity="0.7"/>
      <defs>
        <radialGradient id="ringGradL" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#c8a84b"/>
          <stop offset="100%" stopColor="#c8a84b" stopOpacity="0"/>
        </radialGradient>
        <linearGradient id="ballGradL" x1="48" y1="48" x2="172" y2="172" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#c8a84b" stopOpacity="0.8"/>
          <stop offset="100%" stopColor="#604020" stopOpacity="0.4"/>
        </linearGradient>
        <linearGradient id="topGradL" x1="48" y1="60" x2="172" y2="110" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#c83030"/>
          <stop offset="100%" stopColor="#803010"/>
        </linearGradient>
        <linearGradient id="btnGradL" x1="96" y1="96" x2="124" y2="124" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#f0d060"/>
          <stop offset="100%" stopColor="#c8853a"/>
        </linearGradient>
        <radialGradient id="innerBtnL" cx="40%" cy="35%">
          <stop offset="0%" stopColor="#f0e8d0"/>
          <stop offset="100%" stopColor="#a08060"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

/* ─── Eye icon inline SVG (no image dependency) ─────────────────────────────── */
function EyeIcon({ open }) {
  return open ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

/* ─── Component ─────────────────────────────────────────────────────────────── */
export default function Login() {
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [confirmPw, setConfirmPw]   = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [newUser, setNewUser]       = useState(false);
  const [errorMsg, setErrorMsg]     = useState('');
  const [loading, setLoading]       = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    setErrorMsg('');
    if (newUser && password !== confirmPw) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      if (newUser) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      navigate('/fearlesslocke');
    } catch (err) {
      setErrorMsg(err.message.replace('Firebase: ', '').replace(/\(auth.*\)\.?/, '').trim());
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes floatBob {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-10px); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(24px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        .login-input:focus {
          outline: none;
          border-color: #c8a84b !important;
          background: rgba(200,168,75,0.06) !important;
        }
        .login-input::placeholder { color: #504840; }
        .login-submit:hover:not(:disabled) {
          background: linear-gradient(135deg, #f0d060, #c8853a) !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(200,168,75,0.3) !important;
        }
        .login-submit:disabled { opacity: 0.5; cursor: default; }
        .login-toggle:hover { color: #f0d060 !important; }
      `}</style>

      <div style={S.page}>
        {/* Grain overlay */}
        <div style={S.grain} />

        {/* Card */}
        <div style={S.card}>

          {/* Ball graphic */}
          <div style={S.ballWrap}>
            <HeroGraphic size={120} />
          </div>

          {/* Heading */}
          <p style={S.label}>Fearless Nuzlocke</p>
          <h1 style={S.title}>{newUser ? 'Create Account' : 'Welcome Back'}</h1>
          <p style={S.subtitle}>{newUser ? 'Join the challenge.' : 'Sign in to continue your run.'}</p>

          {/* Error */}
          {errorMsg && <p style={S.error}>{errorMsg}</p>}

          {/* Form */}
          <form onSubmit={handleSubmit} style={S.form}>

            <div style={S.fieldWrap}>
              <label style={S.fieldLabel}>Email</label>
              <input
                className="login-input"
                type="email"
                placeholder="trainer@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={S.input}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.fieldLabel}>Password</label>
              <div style={S.inputRow}>
                <input
                  className="login-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  style={{ ...S.input, paddingRight: '2.5rem' }}
                />
                <button type="button" onClick={() => setShowPassword(p => !p)} style={S.eyeBtn}>
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>

            {newUser && (
              <div style={S.fieldWrap}>
                <label style={S.fieldLabel}>Confirm Password</label>
                <div style={S.inputRow}>
                  <input
                    className="login-input"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPw}
                    onChange={e => setConfirmPw(e.target.value)}
                    required
                    style={{ ...S.input, paddingRight: '2.5rem' }}
                  />
                  <button type="button" onClick={() => setShowConfirm(p => !p)} style={S.eyeBtn}>
                    <EyeIcon open={showConfirm} />
                  </button>
                </div>
              </div>
            )}

            <button type="submit" className="login-submit" disabled={loading} style={S.submit}>
              {loading ? 'Please wait…' : newUser ? 'Register' : 'Sign In'}
            </button>

          </form>

          {/* Toggle */}
          <button
            type="button"
            className="login-toggle"
            onClick={() => { setNewUser(u => !u); setErrorMsg(''); setConfirmPw(''); }}
            style={S.toggle}
          >
            {newUser ? 'Already have an account? Sign in' : "Don't have an account? Register"}
          </button>

        </div>
      </div>
    </>
  );
}

/* ─── Styles ────────────────────────────────────────────────────────────────── */
const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'radial-gradient(ellipse at 50% 40%, #1a2340 0%, #0e1117 70%)',
    padding: '2rem 1rem',
    position: 'relative',
    fontFamily: "'Georgia', 'Times New Roman', serif",
  },
  grain: {
    position: 'fixed', inset: 0, pointerEvents: 'none',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.75\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.04\'/%3E%3C/svg%3E")',
    opacity: 0.6,
    zIndex: 0,
  },
  card: {
    position: 'relative', zIndex: 1,
    width: '100%', maxWidth: '400px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(200,168,75,0.2)',
    borderRadius: '16px',
    padding: '2.5rem 2rem 2rem',
    boxShadow: '0 8px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(200,168,75,0.1)',
    animation: 'cardIn 0.7s ease forwards',
    textAlign: 'center',
  },
  ballWrap: {
    display: 'flex', justifyContent: 'center',
    marginBottom: '1.25rem',
    animation: 'floatBob 4s ease-in-out infinite',
  },
  label: {
    fontSize: '0.7rem', letterSpacing: '0.3em',
    textTransform: 'uppercase', color: '#c8a84b',
    margin: '0 0 0.4rem',
  },
  title: {
    fontSize: '1.8rem', fontWeight: 900,
    margin: '0 0 0.3rem',
    background: 'linear-gradient(135deg, #f0d060, #c8853a)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: {
    fontSize: '0.85rem', color: '#604840',
    margin: '0 0 1.75rem', fontStyle: 'italic',
  },
  error: {
    background: 'rgba(200,50,50,0.12)',
    border: '1px solid rgba(200,50,50,0.3)',
    borderRadius: '6px',
    color: '#e08080',
    fontSize: '0.82rem',
    padding: '0.5rem 0.75rem',
    marginBottom: '1rem',
    textAlign: 'left',
  },
  form: {
    display: 'flex', flexDirection: 'column', gap: '1rem',
    textAlign: 'left',
  },
  fieldWrap: {
    display: 'flex', flexDirection: 'column', gap: '0.3rem',
  },
  fieldLabel: {
    fontSize: '0.72rem', letterSpacing: '0.1em',
    textTransform: 'uppercase', color: '#806050',
  },
  inputRow: {
    position: 'relative', display: 'flex', alignItems: 'center',
  },
  input: {
    width: '100%', padding: '0.65rem 0.85rem',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '7px',
    color: '#e8e0d0', fontSize: '0.95rem',
    transition: 'border-color 0.2s, background 0.2s',
    boxSizing: 'border-box',
  },
  eyeBtn: {
    position: 'absolute', right: '0.65rem',
    background: 'none', border: 'none',
    color: '#604840', cursor: 'pointer',
    display: 'flex', alignItems: 'center',
    padding: 0,
    transition: 'color 0.15s',
  },
  submit: {
    marginTop: '0.5rem',
    padding: '0.75rem',
    background: 'linear-gradient(135deg, #c8a84b, #8a5a20)',
    border: 'none', borderRadius: '8px',
    color: '#0e1117', fontWeight: 800,
    fontSize: '1rem', letterSpacing: '0.05em',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 16px rgba(200,168,75,0.2)',
    fontFamily: "'Georgia', 'Times New Roman', serif",
  },
  toggle: {
    marginTop: '1.25rem',
    background: 'none', border: 'none',
    color: '#604840', fontSize: '0.82rem',
    cursor: 'pointer', textDecoration: 'underline',
    transition: 'color 0.15s',
    fontFamily: "'Georgia', 'Times New Roman', serif",
  },
};